import { Fragment } from 'react';

import {
  ActivityChangeOptions,
  ActivityItem,
  ActivityTagItem,
} from '@spotify/plugin-confidence-activity-react';

import { formatDateRangeFromStrings } from '../utils';
import { createWorkflowUpdatesChangeExtension } from './workflow-updates-change';

interface Period {
  startTime?: string;
  endTime?: string;
  runtime?: number;
}

interface PlanningData {
  planned?: Period;
  actual?: Period;
  [key: string]: unknown;
}

const formatPeriod = (period?: Period): string => {
  if (!period || (!period.startTime && !period.endTime)) {
    return 'Not set';
  }

  if (period.startTime && period.endTime) {
    return formatDateRangeFromStrings(period.startTime, period.endTime);
  }

  if (period.startTime) {
    return `From ${formatDateRangeFromStrings(period.startTime)}`;
  }

  if (period.endTime) {
    return `Until ${formatDateRangeFromStrings(period.endTime)}`;
  }

  return 'Not set';
};

const PlanningChangeRender = ({ change }: ActivityChangeOptions) => {
  const oldValue = change.oldValue as PlanningData | null;
  const newValue = change.newValue as PlanningData | null;

  const oldPlanned = oldValue?.planned;
  const newPlanned = newValue?.planned;
  const oldActual = oldValue?.actual;
  const newActual = newValue?.actual;

  const plannedChanged =
    oldPlanned?.startTime !== newPlanned?.startTime ||
    oldPlanned?.endTime !== newPlanned?.endTime;

  const actualChanged =
    oldActual?.startTime !== newActual?.startTime ||
    oldActual?.endTime !== newActual?.endTime;

  const oldFormatted = formatPeriod(oldPlanned);
  const newFormatted = formatPeriod(newPlanned);

  return (
    <Fragment>
      {plannedChanged && (
        <ActivityItem.Section label="Planned timeline">
          <ActivityTagItem
            tagLabel="Planned"
            oldValue={oldFormatted}
            newValue={newFormatted}
          />
        </ActivityItem.Section>
      )}

      {actualChanged && (
        <ActivityItem.Section label="Actual timeline">
          <ActivityTagItem
            tagLabel="Actual"
            oldValue={formatPeriod(oldActual)}
            newValue={formatPeriod(newActual)}
          />
        </ActivityItem.Section>
      )}
    </Fragment>
  );
};

export const PLANNING_CHANGE_EXTENSION = createWorkflowUpdatesChangeExtension({
  id: 'planning-change',
  predicate: ({ change }) => {
    const matches =
      change.fieldPath === '/module_data2/planning' ||
      change.fieldPath === '/module_data/planning';

    return matches;
  },
  title: change => {
    const oldValue = change.oldValue as PlanningData | null;
    const newValue = change.newValue as PlanningData | null;

    const oldPlanned = oldValue?.planned;
    const newPlanned = newValue?.planned;
    const oldActual = oldValue?.actual;
    const newActual = newValue?.actual;

    const plannedChanged =
      oldPlanned?.startTime !== newPlanned?.startTime ||
      oldPlanned?.endTime !== newPlanned?.endTime;

    const actualChanged =
      oldActual?.startTime !== newActual?.startTime ||
      oldActual?.endTime !== newActual?.endTime;

    if (plannedChanged && actualChanged) {
      return 'Updated timeline';
    }

    if (plannedChanged) {
      return 'Updated planned timeline';
    }

    if (actualChanged) {
      // Starting the experiment
      if (!oldActual?.startTime && newActual?.startTime) {
        return 'Started experiment';
      }
      // Ending the experiment
      if (!oldActual?.endTime && newActual?.endTime) {
        return 'Ended experiment';
      }
      // Other actual timeline changes (manual corrections, etc)
      return 'Updated actual timeline';
    }

    return 'Updated timeline';
  },
  render: PlanningChangeRender,
});
